// The lead agent's results per segment feed the next run (as Explee's retrospective agent, which scales segments by
// cost per lead): the pre_lead_segment_results view's counts label each segment with a suggestion. Founder decision
// 2026-10-27: the agent only suggests; the User applies or dismisses each one, and nothing changes the night plan
// without that click. Both runs then skip a paused segment and search a scaled one's extra pages. Pure.
import type { NightPlan, Segment } from "./leadAgent.ts";

/** One row of the pre_lead_segment_results view */
export type SegmentResult = {
  segment_name: string;
  found: number;
  kept: number;
  removed: number;
  sent: number;
  replied: number;
  /** interested replies, converted pre-leads included */
  interested: number;
  converted: number;
};

export type SegmentLabel = "scaling" | "working" | "paused" | "too_early";
/** The suggestions the User can apply */
export type Applicable = "scaling" | "paused";

// ponytail: fixed thresholds over all-time counts, so last month weighs as much as last week; per-period counts (or
// cost per lead once look-ups are priced) when a segment's history is long enough to tell the difference.
/** Below both samples a segment is too early to judge */
export const MIN_FOUND = 10;
export const MIN_SENT = 10;
/** Interested replies per first email sent at or above which a segment is worth more look-ups */
export const SCALE_INTERESTED_RATE = 0.1;
/** Kept per pre-lead found at or above which a segment is worth more look-ups, before enough emails went out */
export const SCALE_KEEP_RATE = 0.6;
/** Removed as not a fit per pre-lead found at or above which pausing is suggested */
export const PAUSE_REMOVED_RATE = 0.5;
/** The most pages per keyed engine a scaled segment searches (a look-up each, out of the daily cap) */
export const MAX_WEIGHT = 3;

/** The suggestion for a segment and its reason in one plain line ("12 sent, 3 interested") */
export const labelSegment = (
  r: SegmentResult,
): { label: SegmentLabel; reason: string } => {
  const sent = `${r.sent} sent, ${r.interested} interested`;
  const found = `${r.found} found, ${r.kept} kept, ${r.removed} not a fit`;
  // replies say more than reviews: enough emails out decides first
  if (r.sent >= MIN_SENT) {
    if (r.interested / r.sent >= SCALE_INTERESTED_RATE)
      return { label: "scaling", reason: sent };
    if (!r.interested) return { label: "paused", reason: sent };
  }
  if (r.found >= MIN_FOUND) {
    if (r.removed / r.found >= PAUSE_REMOVED_RATE)
      return { label: "paused", reason: found };
    if (r.sent < MIN_SENT && r.kept / r.found >= SCALE_KEEP_RATE)
      return { label: "scaling", reason: found };
  } else if (r.sent < MIN_SENT)
    return { label: "too_early", reason: `${r.found} found, ${r.sent} sent` };
  return { label: "working", reason: r.sent >= MIN_SENT ? sent : found };
};

/** The pages per keyed engine the User gave a segment (1 to MAX_WEIGHT) */
export const weightOf = (
  plan: Pick<NightPlan, "weights"> | undefined,
  name: string,
) =>
  Math.min(
    MAX_WEIGHT,
    Math.max(1, Math.round(Number(plan?.weights?.[name]) || 1)),
  );

// ponytail: segments match by name; a segment the AI renames starts unpaused at weight 1
export const isPaused = (
  plan: Pick<NightPlan, "paused"> | undefined,
  name: string,
) => !!plan?.paused?.includes(name);

/** The night plan after the User applies a suggestion: Scaling adds a page per keyed engine (up to MAX_WEIGHT) and
 * unpauses the segment; Paused adds it to the names neither run searches */
export const applySuggestion = (
  plan: NightPlan | undefined,
  name: string,
  label: Applicable,
): NightPlan => {
  const base = plan ?? { offer: "", segments: [] };
  const paused = (base.paused ?? []).filter((n) => n !== name);
  if (label === "paused") return { ...base, paused: [...paused, name] };
  return {
    ...base,
    paused,
    weights: {
      ...base.weights,
      [name]: Math.min(MAX_WEIGHT, weightOf(base, name) + 1),
    },
  };
};

/** The segments ticked when the agent proposes a brief: all but the ones the User paused */
export const unpausedIndexes = (
  segments: Segment[],
  plan: Pick<NightPlan, "paused"> | undefined,
) => new Set(segments.flatMap((s, i) => (isPaused(plan, s.name) ? [] : [i])));

/** The night plan a search saves: the segments searched (a paused one the User ticked again is no longer paused);
 * the other pauses and the weights stay */
export const searchedPlan = (
  saved: NightPlan | undefined,
  offer: string,
  segments: Segment[],
): NightPlan => ({
  ...saved,
  offer,
  segments,
  paused: (saved?.paused ?? []).filter(
    (n) => !segments.some((s) => s.name === n),
  ),
});
